import React, {FC, useState} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {buildCancelTokenSource, CancelTokenSource} from '../../../../../../../src/helpers/asyncCancelHelpers';
import {contactsManagerAccountsUrl} from '../../../../../../../src/helpers/contactsManagerHelpers';
import {ResolveDispatchProps} from '../../../../../../../src/helpers/reduxHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../src/helpers/reselectHelpers';
import {
  addCardsToAccountAction,
  fetchAccountAsyncAction,
} from '../../../../../../../src/store/data/accounts/accountsActions';
import {getAccounts} from '../../../../../../../src/store/data/dataSelectors';
import {
  makeGetAccountId,
  makeGetSelectedCardIds,
} from '../../../../../../../src/store/panels/contacts/contactsManager/contactsManagerPanelSelectors';
import {closeSidePanelAction} from '../../../../../../../src/store/panels/contacts/contactsManager/sidePanel/contactsManagerSidePanelActions';
import {ContactsManagerPanelId} from '../../../../../../../src/store/panels/panelStateBase';
import {navigateToLinkAction} from '../../../../../../../src/store/stateless/navigationActions';
import {useInitialEffect} from '../../../../../../shared/helpers/src/react/hooks/useEffectHelpers';
import {AccountContactsPage} from './accountContactsPage';

/*
 * Props.
 */

interface AccountContactsPageContainerComponentPublicProps {
  panelId: ContactsManagerPanelId;
}
type AccountContactsPageContainerComponentStateProps = ResolveStateProps<typeof mapStateToProps>;
type AccountContactsPageContainerComponentDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type AccountContactsPageContainerComponentProps = AccountContactsPageContainerComponentPublicProps &
  AccountContactsPageContainerComponentStateProps &
  AccountContactsPageContainerComponentDispatchProps;

/*
 * Selectors.
 */

function makeGetAccount(getAccountId: ReturnType<typeof makeGetAccountId>) {
  return createSelector([getAccountId, getAccounts], (accountId, accounts) => {
    if (!accountId) {
      return undefined;
    }
    return accounts[accountId];
  });
}

/*
 * Component.
 */

const AccountContactsPageContainerComponent: FC<AccountContactsPageContainerComponentProps> = (props) => {
  const {
    panelId,
    account,
    accountId,
    selectedCardIds,
    fetchAccountAsync,
    navigateToLink,
    addCardsToAccount,
    closeSidePanel,
  } = props;
  const [cancelTokenSource, setCancelTokenSource] = useState<CancelTokenSource | undefined>(undefined);

  const navigateBackToAccounts = () => navigateToLink(contactsManagerAccountsUrl);

  // Check if we need to fetch the accounts details as we may not have the account in the redux store.
  useInitialEffect(() => {
    async function fetchAccountData() {
      if (account || !accountId) {
        return;
      }

      try {
        const newCancelTokenSource = buildCancelTokenSource();
        setCancelTokenSource(newCancelTokenSource);
        const foundAccount = await fetchAccountAsync(accountId, {cancelToken: newCancelTokenSource.token});

        // Failed to find the account, return to the accounts page
        /** TODO: Show unable to find account to user (?) */
        if (!foundAccount) {
          navigateBackToAccounts();
        }
      } catch (err) {
        /** TODO: Show error to the user */
        navigateBackToAccounts();
      }
      setCancelTokenSource(undefined);
    }
    fetchAccountData();

    // If they navigate away before viewing, cancel the fetch
    return () => {
      closeSidePanel();
      if (cancelTokenSource) {
        cancelTokenSource.cancel();
      }
    };
  });

  return (
    <AccountContactsPage
      panelId={panelId}
      account={account}
      selectedCardIds={selectedCardIds}
      navigateBackToAccounts={navigateBackToAccounts}
      addCardsToAccount={addCardsToAccount}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<AccountContactsPageContainerComponentPublicProps>()(() => {
  const getAccountId = makeGetAccountId();
  return {
    accountId: getAccountId,
    account: makeGetAccount(getAccountId),
    selectedCardIds: makeGetSelectedCardIds(),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<AccountContactsPageContainerComponentPublicProps>()(() => ({
  fetchAccountAsync: fetchAccountAsyncAction,
  navigateToLink: navigateToLinkAction,
  addCardsToAccount: addCardsToAccountAction,
  closeSidePanel: closeSidePanelAction,
}));

export const AccountContactsPageContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AccountContactsPageContainerComponent);
